import React, { useCallback, useEffect, useMemo, useReducer, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import useAxios from 'axios-hooks'
import { produce } from 'immer'

import { Button, ButtonVariant } from '@amzn/stencil-react-components/button'
import { Expander } from '@amzn/stencil-react-components/expander'
import { Col, Container, Hr, Row, Spacer, View } from '@amzn/stencil-react-components/layout'
import { Modal } from '@amzn/stencil-react-components/modal'
import { SortDirection } from '@amzn/stencil-react-components/table'
import { H2, H3, H4, Text } from '@amzn/stencil-react-components/text'
import { IconAlertCircleFillMedium as IconAlertCircleFillMedium } from '@amzn/stencil-react-icons'

import { ResponsiveRow } from 'src/components/ResponsiveRow'
import {
    ModuleGroupSortCriteria,
    ModuleGroupStatus,
    ModuleGroupType,
    PageOfModuleGroups,
    SearchQuery,
} from 'src/models/dto/module-groups/ModuleGroupTypeDTO'
import { focusOnDataTestId, focusOnId, focusOnSortedHeader } from 'src/pages/module-builder/focus'
import { DuplicateModuleGroupModal } from 'src/pages/module-groups/DuplicateModuleGroupModal'
import { ModuleGroupBuilderModal } from 'src/pages/module-groups/ModuleGroupBuilderModal'
import { ModuleGroupsFilter } from 'src/pages/module-groups/ModuleGroupsFilter'
import { DuplicateOptionsProps, ModuleGroupTable } from 'src/pages/module-groups/ModuleGroupTable'
import { fromParams, getSearchRequest, toSearchParams } from 'src/pages/module-groups/searchParams'

export type {
    ModuleGroupSortField,
    ModuleGroupSortCriteria,
    PageOfModuleGroups,
    SearchQuery,
} from 'src/models/dto/module-groups/ModuleGroupTypeDTO'

const EquivalencyTypesInfo = () => (
    <Expander shouldExpandOnMount={true} titleText={'What are the equivalency types'}>
        <Container paddingHorizontal={'S600'} paddingBottom={'S400'}>
            <Text fontSize={'T200'} fontWeight={'regular'}>
                <strong>Measurement Equivalent Scores (MES)-</strong> Use this score group when
                there is a module (the reference module) that has equivalent module level scores in
                another module(s) that could be reused for a candidate, such that the reference
                module would not need to be presented.
            </Text>
            <Spacer height={'S400'} />
            <Text fontSize={'T200'} fontWeight={'regular'}>
                <strong>Presentation Equivalent Content (PEC)-</strong> Use this group when the
                module (evaluation or non-evaluation) that should be presented to a candidate
                differs based on job metadata (eg., job code, job level, job family).
            </Text>
            <Spacer height={'S400'} />
            <Text fontSize={'T200'} fontWeight={'regular'}>
                <strong>Functionality Equivalent Scores (FES)-</strong> This score group is a
                sub-feature of PEC. Use this score group if the PEC contains evaluation modules that
                have module level scores that will function identically in the workflow level
                overall score formula. These module level scores are not measurement equivalent and
                can not be substituted with each other, but they do have the same mathematical role
                in the workflow level overall score formula.
            </Text>
        </Container>
    </Expander>
)

interface ModuleGroupsPageInnerProps {
    searchQuery: SearchQuery
    setSearchQuery: React.Dispatch<React.SetStateAction<SearchQuery>>
}

export const ModuleGroupsPageBody = ({
    searchQuery,
    setSearchQuery,
}: ModuleGroupsPageInnerProps) => {
    // nextSearchQuery is the work-in-progress search query
    // calling triggerSearch writes nextSearchQuery back into searchQuery
    const [nextSearchQuery, setNextSearchQuery] = useState<SearchQuery>(searchQuery)
    const [triggerSearchCounter, incrementCounter] = useReducer((x: number) => x + 1, 0)
    const [writeBack, setWriteBack] = useState(false)
    useEffect(() => {
        if (writeBack) {
            setSearchQuery(nextSearchQuery)
            incrementCounter()
            setWriteBack(false)
        }
    }, [writeBack, nextSearchQuery, setSearchQuery])
    const setSearchQueryAndTriggerSearch = useCallback(
        (update: React.SetStateAction<SearchQuery>) => {
            setNextSearchQuery(update)
            setWriteBack(true)
        },
        []
    )

    const [currentPage, setCurrentPage] = useState(
        Math.floor(searchQuery.offset / searchQuery.numberOfRecordsPerPage) + 1
    )
    const [focus, setFocus] = useState<string>('')
    const [showDuplicateModal, setShowDuplicateModal] = useState<boolean>(false)
    const [selectedModuleGroupId, setSelectedModuleGroupId] = useState<string>('')
    const [selectedModuleGroupName, setSelectedModuleGroupName] = useState<string>('')

    const [{ loading, data, error }, executeSearchRequest] = useAxios<PageOfModuleGroups>(
        {},
        { useCache: false, manual: true }
    )
    useEffect(() => {
        void executeSearchRequest(getSearchRequest(searchQuery))
            .catch((e) => {
                const typeName = (e as Error).constructor.name
                if (typeName === 'Cancel' || typeName === 'CanceledError') {
                    return
                }
                console.error('search error', e)
            })
            .then(() => {
                if (focus.length > 0 && focus === 'sort') {
                    focusOnSortedHeader()
                } else if (focus.length && focus === 'pageSize') {
                    focusOnId('items-per-page-select-toggle-button')
                } else if (focus.length && focus === 'pageNumber') {
                    focusOnDataTestId(currentPage.toString())
                }
                setFocus('')
            })
    }, [triggerSearchCounter, executeSearchRequest, searchQuery, focus, currentPage])

    const duplicateOptionsProps = {
        setSelectedModuleGroupName: setSelectedModuleGroupName,
        setShowDuplicateModal: setShowDuplicateModal,
        setSelectedModuleGroupId: setSelectedModuleGroupId,
    } as DuplicateOptionsProps

    const Results = () => {
        if (error) {
            return (
                <Col
                    display='flex'
                    justifyContent='flex-start'
                    width='100%'
                    alignItems='center'
                    gridGap='S200'
                    padding={{ top: 'S200' }}
                >
                    <Row gridGap={'S200'} alignItems={'center'}>
                        <IconAlertCircleFillMedium color={'red70'} />
                        <Text fontSize={'T500'} textAlign={'center'} color='neutral90'>
                            Oops, an unexpected error occurred on our end.
                        </Text>
                    </Row>
                    <Text fontSize={'T400'} color='neutral70'>
                        Please try searching again.
                    </Text>
                </Col>
            )
        } else if ((data && data.moduleGroupMetadataList.length > 0) || loading) {
            return (
                <ModuleGroupTable
                    data={data?.moduleGroupMetadataList ?? []}
                    tableType='moduleGroup'
                    pageSize={searchQuery.numberOfRecordsPerPage}
                    totalRecords={data?.totalRecords ?? 0}
                    loading={loading}
                    selectedPage={currentPage}
                    setPage={(page: number) => {
                        const newOffset = (page - 1) * searchQuery.numberOfRecordsPerPage
                        setCurrentPage(page)
                        setSearchQueryAndTriggerSearch(
                            produce((draft) => {
                                draft.offset = newOffset
                            })
                        )
                        setFocus('pageNumber')
                    }}
                    updatePageSize={(passedPageSize: number) => {
                        setSearchQueryAndTriggerSearch(
                            produce((draft) => {
                                draft.numberOfRecordsPerPage = passedPageSize
                                draft.offset = 0
                            })
                        )
                        setCurrentPage(1)
                        setFocus('pageSize')
                    }}
                    sortCriteria={{
                        sortField: searchQuery.sortField ?? 'modified_at',
                        sortDirection:
                            (searchQuery.sortDirection as SortDirection) ??
                            SortDirection.Descending,
                    }}
                    onSortCriteriaChange={(newSortCriteria: ModuleGroupSortCriteria) => {
                        setSearchQueryAndTriggerSearch(
                            produce((draft) => {
                                draft.sortField = newSortCriteria.sortField
                                draft.sortDirection = newSortCriteria.sortDirection
                                draft.offset = 0
                            })
                        )
                        setCurrentPage(1)
                        setFocus('sort')
                    }}
                    duplicateOptions={duplicateOptionsProps}
                />
            )
        }
        return (
            <Col
                display='flex'
                justifyContent='flex-start'
                width='100%'
                alignItems='center'
                gridGap='S200'
                padding={{ top: 'S200' }}
                dataTestId={'no-results-component'}
            >
                <H4 color='neutral70' textAlign='center' data-no-search-results>
                    No module groups were found with these parameters. Please refine the search or
                    create a module group that matches these filters and try again.
                </H4>
            </Col>
        )
    }

    const applyFilters = useCallback(() => {
        setSearchQueryAndTriggerSearch(
            produce((draft) => {
                draft.offset = 0
            })
        )
        setCurrentPage(1)
    }, [setSearchQueryAndTriggerSearch])

    const resetFilters = useCallback(() => {
        setSearchQueryAndTriggerSearch(
            produce((draft) => {
                draft.search = ''
                draft.moduleVersionId = undefined
                draft.moduleGroupType = []
                draft.moduleGroupStatus = []
                draft.startDate = undefined
                draft.endDate = undefined
                draft.owner = undefined
                draft.offset = 0
            })
        )
        setCurrentPage(1)
    }, [setSearchQueryAndTriggerSearch])

    const [showModuleGroupBuilderPage, setShowModuleGroupBuilderPage] = useState<boolean>(false)
    return (
        <Container>
            <Col gridGap='S300'>
                <View padding={{ top: 'S300', left: 'S300', right: 'S300', bottom: 'S200' }}>
                    <H2>Module Groups</H2>
                </View>
                <EquivalencyTypesInfo />
                <Hr size='wide' />
                <ResponsiveRow gridGap={'S300'}>
                    <Col flex='0 0 300px'>
                        <H3 id={'current-module-group-filter-header'}>Filters</H3>
                        <Spacer height={'S400'} />
                        <ModuleGroupsFilter
                            moduleGroupName={nextSearchQuery.search}
                            typeOfEquivalency={nextSearchQuery.moduleGroupType ?? []}
                            moduleVersionId={nextSearchQuery.moduleVersionId ?? ''}
                            status={nextSearchQuery.moduleGroupStatus ?? []}
                            startDate={nextSearchQuery.startDate ?? ''}
                            endDate={nextSearchQuery.endDate ?? ''}
                            ownerOfRecord={nextSearchQuery.owner ?? ''}
                            updateGroupName={(value: string) =>
                                setNextSearchQuery(
                                    produce((draft) => {
                                        draft.search = value
                                    })
                                )
                            }
                            updateModuleVersionId={(value: string) =>
                                setNextSearchQuery(
                                    produce((draft) => {
                                        draft.moduleVersionId = value || undefined
                                    })
                                )
                            }
                            updateOwnerOfRecord={(value: string) =>
                                setNextSearchQuery(
                                    produce((draft) => {
                                        draft.owner = value || undefined
                                    })
                                )
                            }
                            updateStatus={(value: ModuleGroupStatus[]) =>
                                setNextSearchQuery(
                                    produce((draft) => {
                                        draft.moduleGroupStatus = value
                                    })
                                )
                            }
                            updateStartDate={(value: string) =>
                                setNextSearchQuery(
                                    produce((draft) => {
                                        draft.startDate = value || undefined
                                    })
                                )
                            }
                            updateEndDate={(value: string) =>
                                setNextSearchQuery(
                                    produce((draft) => {
                                        draft.endDate = value || undefined
                                    })
                                )
                            }
                            updateTypeOfEquivalency={(value: ModuleGroupType[]) =>
                                setNextSearchQuery(
                                    produce((draft) => {
                                        draft.moduleGroupType = value
                                    })
                                )
                            }
                            applyFilters={applyFilters}
                            resetFilters={resetFilters}
                            sideFilter={true}
                        />
                    </Col>
                    <Col flex='3 1 auto'>
                        <ResponsiveRow>
                            <H3 id={'current-module-group-header'}>
                                <strong>Current Module Groups</strong>
                            </H3>
                            <Spacer flex={1} />
                            <Button
                                dataTestId={'add-module-group-button'}
                                onClick={() => setShowModuleGroupBuilderPage(true)}
                                variant={ButtonVariant.Primary}
                            >
                                Add Module Group
                            </Button>
                        </ResponsiveRow>
                        <Spacer height={'S400'} />
                        <Results />
                    </Col>
                </ResponsiveRow>
                <ModuleGroupBuilderModal
                    showModuleGroupBuilder={showModuleGroupBuilderPage}
                    setShowModuleGroupBuilder={setShowModuleGroupBuilderPage}
                />

                <Modal
                    isOpen={showDuplicateModal}
                    close={() => {
                        setShowDuplicateModal(false)
                        focusOnId(`duplicate-${selectedModuleGroupName}`)
                    }}
                    isScrollable={false}
                >
                    <DuplicateModuleGroupModal
                        selectedModuleGroupId={selectedModuleGroupId}
                        selectedModuleGroupName={selectedModuleGroupName}
                        close={() => setShowDuplicateModal(false)}
                    />
                </Modal>
            </Col>
        </Container>
    )
}

export const ModuleGroupsPage = () => {
    const [urlSearchParams, setUrlSearchParams] = useSearchParams()

    // Convert URLSearchParams to SearchQuery
    const urlSearchQuery = useMemo(() => fromParams(urlSearchParams), [urlSearchParams])
    const setSearchQuery: React.Dispatch<React.SetStateAction<SearchQuery>> = useCallback(
        (newValue) => {
            const newSearchQuery =
                typeof newValue === 'function' ? newValue(urlSearchQuery) : newValue
            const currentPage =
                Math.floor(newSearchQuery.offset / newSearchQuery.numberOfRecordsPerPage) + 1
            const newSearchParams = toSearchParams(newSearchQuery, currentPage)
            setUrlSearchParams(newSearchParams)
        },
        [setUrlSearchParams, urlSearchQuery]
    )

    return <ModuleGroupsPageBody searchQuery={urlSearchQuery} setSearchQuery={setSearchQuery} />
}
